import { watch, type WatchStopHandle } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { storeToRefs } from 'pinia';
import { useCarsStore } from '@/stores/useCarsStore';
import { useVariablesStore, type StepName } from '@/stores/useVariablesStore';

type SelectionState = {
  year?: string;
  make?: string;
  model?: string;
  trim?: string;
  color?: string;
};

/**
 * Path depth → landing step:
 * - until model → Trims (init trim defaults + write trim to path)
 * - until trim (or color) → Options
 * - otherwise → Models
 */
function getStepFromPath(s: SelectionState): StepName {
  if (s.trim || s.color) return 'Options';
  if (s.model) return 'Trims';
  return 'Models';
}

// Ordered — the path is built in this order and truncated at the first missing value
const SELECTION_KEYS = ['year', 'make', 'model', 'trim', 'color'] as const;
type SelectionKey = (typeof SELECTION_KEYS)[number];

let hydratePromise: Promise<string> | null = null;
let syncingFromPath = false;
let watchStop: WatchStopHandle | null = null;

function normName(value: unknown): string {
  return String(value ?? '')
    .trim()
    .toLowerCase();
}

function findByName<T>(items: T[], getName: (item: T) => unknown, target: unknown): T | undefined {
  const needle = normName(target);
  if (!needle) return undefined;
  return items.find((item) => normName(getName(item)) === needle);
}

/** Parse /year/2024/make/Ford/model/Maverick/trim/12345/color/Oxford%20White into an object */
function parseSelectionPath(path: string): SelectionState {
  const segments = path.split('/').filter(Boolean);
  const result: SelectionState = {};
  for (let i = 0; i + 1 < segments.length; i += 2) {
    const key = segments[i] as SelectionKey;
    const value = decodeURIComponent(segments[i + 1]);
    if ((SELECTION_KEYS as readonly string[]).includes(key) && value) {
      result[key] = value;
    }
  }
  return result;
}

/** Build /year/2024/make/ford/model/maverick/... stopping at the first missing value */
function buildSelectionPath(state: SelectionState): string {
  const parts: string[] = [];
  for (const key of SELECTION_KEYS) {
    const value = state[key];
    if (!value) break; // stop at the first gap so we don't emit /year/2024/model/maverick
    // trim is a numeric ID — keep as-is; everything else is lowercased
    const encoded = key === 'trim' ? encodeURIComponent(value) : encodeURIComponent(value.toLowerCase());
    parts.push(key, encoded);
  }
  return parts.length ? '/' + parts.join('/') : '/';
}

function sameState(a: SelectionState, b: SelectionState): boolean {
  return SELECTION_KEYS.every((key) => {
    if (key === 'trim') return String(a[key] ?? '') === String(b[key] ?? '');
    return normName(a[key]) === normName(b[key]);
  });
}

function isHomePath(path: string): boolean {
  return !path.startsWith('/admin') && !path.startsWith('/about') && !path.startsWith('/missing-trims');
}

export function useSelectionQuery() {
  const route = useRoute();
  const router = useRouter();
  const carStore = useCarsStore();
  const variablesStore = useVariablesStore();
  const {
    selectedYear,
    selectedMake,
    selectedModel,
    modelVariantId,
    selectedColor,
    selectedModalDetails,
  } = storeToRefs(carStore);
  function getSelectedTrimId(): string | undefined {
    if (modelVariantId.value == null) return undefined;
    return String(modelVariantId.value);
  }

  function getSelectedColorName(): string | undefined {
    if (!selectedColor.value) return undefined;
    const match = carStore.resolveExteriorColor(selectedColor.value);
    const description = match?.descriptions?.[0]?.description;
    if (description) return String(description);
    return String(selectedColor.value);
  }

  function buildCurrentState(): SelectionState {
    const state: SelectionState = {};
    if (selectedYear.value != null) state.year = String(selectedYear.value);
    if (selectedMake.value?.name) state.make = selectedMake.value.name;
    if (selectedModel.value?.name) state.model = selectedModel.value.name;
    const trimId = getSelectedTrimId();
    if (trimId) state.trim = trimId;
    const colorName = getSelectedColorName();
    if (colorName) state.color = colorName;
    return state;
  }

  /**
   * Keep existing path segment text when it still refers to the same selection
   * (avoids rewriting oxford%20white ↔ chrome codes, or casing churn).
   */
  function preservePathParamValues(
    nextState: SelectionState,
    currentState: SelectionState,
  ): SelectionState {
    const merged: SelectionState = { ...nextState };
    for (const key of SELECTION_KEYS) {
      if (!nextState[key] || !currentState[key]) continue;
      if (key === 'trim') {
        if (String(nextState[key]) === String(currentState[key])) {
          merged[key] = currentState[key];
        }
        continue;
      }
      if (normName(nextState[key]) === normName(currentState[key])) {
        merged[key] = currentState[key];
      }
    }
    if (nextState.color && currentState.color && selectedColor.value) {
      const selected = carStore.resolveExteriorColor(selectedColor.value);
      const urlMatchesSelected =
        normName(currentState.color) === normName(selected?.descriptions?.[0]?.description) ||
        normName(currentState.color) === normName(selectedColor.value);
      if (urlMatchesSelected) {
        merged.color = currentState.color;
      }
    }
    return merged;
  }

  function syncQueryToUrl(options?: { includeColor?: boolean }) {
    if (syncingFromPath) return;
    if (!isHomePath(route.path)) return;

    const currentState = parseSelectionPath(route.path);
    const nextState = preservePathParamValues(buildCurrentState(), currentState);

    // Trims step only writes year/make/model/trim — never color
    const includeColor =
      options?.includeColor ?? variablesStore.currentStep !== 'Trims';
    if (!includeColor) {
      delete nextState.color;
    }

    if (sameState(nextState, currentState)) return;

    const newPath = buildSelectionPath(nextState);
    router.replace({
      path: newPath,
      query: route.query, // preserve any unrelated query params
    });
  }

  /** Landing step from URL path variables. */
  function getRestoredStepName(fromPath?: SelectionState): StepName {
    return getStepFromPath(fromPath ?? parseSelectionPath(route.path));
  }

  async function hydrateFromQuery() {
    const s = parseSelectionPath(route.path);
    const hasPath = Boolean(s.year || s.make || s.model || s.trim || s.color);
    const targetStep = getStepFromPath(s);

    await carStore.getYears();

    if (!hasPath) {
      variablesStore.setCurrentStep('Models');
      return 'Models';
    }

    syncingFromPath = true;
    try {
      const year = Number(s.year);
      if (!Number.isFinite(year) || !carStore.years.includes(year)) {
        variablesStore.setCurrentStep('Models');
        return 'Models';
      }

      carStore.$patch({
        selectedYear: year,
        selectedMake: null,
        selectedModel: null,
        selectedModalDetails: null,
        modelVariantId: null,
        selectedColor: null,
      });
      variablesStore.setCurrentStep('Models');

      await carStore.getDivisions({ preserveSelection: true });

      const make = findByName(
        carStore.divisions,
        (d) => d.value?.name ?? d.title,
        s.make,
      )?.value;
      if (!make) {
        variablesStore.setCurrentStep('Models');
        return 'Models';
      }
      carStore.$patch({ selectedMake: make });

      await carStore.getModels({ preserveSelection: true });

      const model = findByName(
        carStore.models,
        (m) => m.value?.name ?? m.title,
        s.model,
      )?.value;
      if (!model) {
        variablesStore.setCurrentStep('Models');
        return 'Models';
      }
      carStore.$patch({ selectedModel: model });
      await carStore.getSelectedModel();
      await carStore.getCompareDetails();

      const styles = selectedModalDetails.value;
      const trimId = Number(s.trim);
      const style =
        Number.isFinite(trimId) && trimId > 0
          ? (styles ?? []).find((item) => Number(item.id) === trimId)
          : undefined;

      if (targetStep === 'Trims' || !style) {
        variablesStore.setCurrentStep('Trims');
        return 'Trims';
      }

      carStore.$patch({ modelVariantId: style.id ?? null });
      await carStore.getConfigurationDetails();
      await carStore.getStyleImages();

      if (s.color) {
        const colorOptions = carStore.getExteriorColorOptions();
        const colorMatch =
          findByName(
            colorOptions,
            (option) => option.descriptions?.[0]?.description,
            s.color,
          ) ||
          colorOptions.find(
            (option) => normName(option.chromeOptionCode) === normName(s.color),
          );
        if (colorMatch?.chromeOptionCode) {
          await carStore.applyExteriorColor(colorMatch.chromeOptionCode);
        } else {
          carStore.syncExteriorColorPreview();
        }
      } else {
        carStore.syncExteriorColorPreview();
      }

      variablesStore.setCurrentStep('Options');
      return 'Options';
    } finally {
      syncingFromPath = false;
    }
  }

  function ensureHydrated() {
    if (!hydratePromise) {
      hydratePromise = hydrateFromQuery();
    }
    return hydratePromise;
  }

  function watchSelections() {
    // Always (re)bind — remounts / new router instances must not keep a dead watcher.
    // Only watch user selection fields — not configuration payload (avoids path rewrites).
    watchStop?.();
    watchStop = watch(
      [selectedYear, selectedMake, selectedModel, modelVariantId, selectedColor],
      () => {
        if (syncingFromPath) return;
        syncQueryToUrl();
      },
      { deep: true },
    );
  }

  return {
    ensureHydrated,
    hydrateFromQuery,
    syncQueryToUrl,
    watchSelections,
    getRestoredStepName,
  };
}
